import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Per-addon field metadata: how each custom value is labelled/rendered for the client.
// kind 'count' -> "50 posts"; kind 'text' -> "Platforms: Instagram, Facebook".
const FIELD_META = {
  social_media: [
    { field: 'posts', unit: 'posts', kind: 'count' },
    { field: 'reels', unit: 'reels', kind: 'count' },
    { field: 'stories', unit: 'stories', kind: 'count' },
    { field: 'platforms', label: 'Platforms', kind: 'text' },
  ],
  paid_ads: [
    { field: 'ad_spend_limit', label: 'Ad spend', kind: 'text' },
    { field: 'campaign_limit', unit: 'campaigns', kind: 'count' },
    { field: 'platforms', label: 'Platforms', kind: 'text' },
  ],
  ai_video: [
    { field: 'videos', unit: 'videos', kind: 'count' },
  ],
  website_creation: [
    { field: 'pages', unit: 'pages', kind: 'count' },
    { field: 'package', label: 'Package', kind: 'text' },
  ],
  product_dev: [
    { field: 'project_tier', label: 'Tier', kind: 'text' },
    { field: 'development_hours', unit: 'hours', kind: 'count' },
    { field: 'special_scope', label: 'Scope', kind: 'text' },
  ],
  legal_translations: [
    { field: 'special_rate', label: 'Rate / agreement', kind: 'text' },
  ],
};

function hasValue(v, kind) {
  if (v === null || v === undefined || v === '') return false;
  if (kind === 'count') {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  }
  return String(v).trim().length > 0;
}

/**
 * Client-facing "what's included" panel for an add-on. Reads the Super Admin's
 * custom allowances from the firm's plan_config.addons_custom[addonKey] and
 * renders them. Shows nothing if the admin hasn't set any custom values.
 */
export default function AddonEntitlements({ firmId, addonKey }) {
  const [vals, setVals] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!firmId) return undefined;
    (async () => {
      try {
        const res = await axios.get(`${API}/firms/${firmId}/plan-config`, { headers: auth() });
        const custom = res.data?.addons_custom?.[addonKey] || {};
        if (!cancelled) setVals(custom);
      } catch (_e) {
        if (!cancelled) setVals({});
      }
    })();
    return () => { cancelled = true; };
  }, [firmId, addonKey]);

  const meta = FIELD_META[addonKey] || [];
  const items = meta.filter((m) => hasValue(vals?.[m.field], m.kind));

  if (!vals || items.length === 0) return null;

  return (
    <div
      className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50/60 p-4 sm:p-5"
      data-testid={`addon-entitlements-${addonKey}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-sm">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="text-sm font-bold text-slate-900">What your plan includes</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((m) => {
          const v = vals[m.field];
          const text = m.kind === 'count'
            ? `${Number(v).toLocaleString('en-US')} ${m.unit}`
            : `${m.label}: ${v}`;
          return (
            <span
              key={m.field}
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-violet-200 text-sm font-medium text-slate-800 shadow-sm"
              data-testid={`addon-entitlement-${addonKey}-${m.field}`}
            >
              {text}
            </span>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 mt-3">Allowances set by your account manager.</p>
    </div>
  );
}
